import React, { useState, useEffect } from 'react';
import {
  Target,
  Server,
  Workflow,
  Shield,
  Building2,
  Users,
  Sparkles,
  ArrowRight,
  CheckCircle,
  Cloud,
  GitBranch,
  Brain,
  Database,
  Zap,
  Lock,
  Code
} from 'lucide-react';

const Home2FeaturesSection = () => {
  const [hoveredCard, setHoveredCard] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('business');

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const section = document.getElementById('features-section');
    if (section) observer.observe(section);

    return () => observer.disconnect();
  }, []);

  const businessFeatures = [
    {
      title: 'AI Transformation Consulting',
      desc: 'End-to-end consulting to identify and implement AI use cases across your organization.',
      icon: <Target className="w-6 h-6" />,
      color: 'from-purple-500 to-blue-500',
      benefit: 'Strategic Roadmap'
    },
    {
      title: 'On-Premises Deployment',
      desc: 'Full data sovereignty with secure on-premises installation for maximum control.',
      icon: <Server className="w-6 h-6" />,
      color: 'from-blue-500 to-cyan-500',
      benefit: 'Complete Control'
    },
    {
      title: 'Multi-Agent Platform',
      desc: 'Create and manage unlimited AI agents and chatbots from a single unified platform.',
      icon: <Workflow className="w-6 h-6" />,
      color: 'from-green-500 to-emerald-500',
      benefit: 'Unified System'
    },
    {
      title: 'Enterprise Security',
      desc: 'Bank-grade security with SSO, role-based access, and complete audit trails.',
      icon: <Shield className="w-6 h-6" />,
      color: 'from-orange-500 to-red-500',
      benefit: 'SOC 2 Ready'
    },
    {
      title: 'Hybrid & Cloud Options',
      desc: 'Flexible deployment models including cloud, hybrid, and private cloud solutions.',
      icon: <GitBranch className="w-6 h-6" />,
      color: 'from-pink-500 to-rose-500',
      benefit: 'Your Choice'
    },
    {
      title: 'Enterprise Integration',
      desc: 'Seamlessly connect with your existing systems, databases, and enterprise applications.',
      icon: <Building2 className="w-6 h-6" />,
      color: 'from-indigo-500 to-purple-500',
      benefit: 'Plug & Play'
    },
  ];

  const platformFeatures = [
    {
      title: 'Multi-LLM Intelligence',
      desc: 'Dynamically switch between 15+ models including GPT-4, Claude, and LLaMA for optimal results.',
      icon: <Brain className="w-6 h-6" />,
      color: 'from-purple-500 to-pink-500',
      benefit: '15+ Models'
    },
    {
      title: 'Vector Memory Store',
      desc: 'Intelligent semantic search with 99.9% accuracy for context-aware agent responses.',
      icon: <Database className="w-6 h-6" />,
      color: 'from-blue-500 to-cyan-500',
      benefit: 'Smart Recall'
    },
    {
      title: 'Visual Agent Builder',
      desc: 'Drag-and-drop interface to build LLM, webhook, email, and API trigger agents without code.',
      icon: <Code className="w-6 h-6" />,
      color: 'from-green-500 to-emerald-500',
      benefit: 'No Code'
    },
    {
      title: 'Workflow Orchestration',
      desc: 'Event-driven automation with conditional routing, scheduling, and parallel execution.',
      icon: <Zap className="w-6 h-6" />,
      color: 'from-orange-500 to-yellow-500',
      benefit: 'Real-time'
    },
    {
      title: 'Role-Based Access Control',
      desc: 'Granular security with department-level restrictions, JWT authentication, and audit logs.',
      icon: <Lock className="w-6 h-6" />,
      color: 'from-red-500 to-pink-500',
      benefit: 'Zero Trust'
    },
    {
      title: 'API-First Architecture',
      desc: 'Complete programmatic control with REST/GraphQL APIs and webhook integrations.',
      icon: <Cloud className="w-6 h-6" />,
      color: 'from-indigo-500 to-purple-500',
      benefit: 'Full Control'
    },
  ];

  const activeFeatures = activeTab === 'business' ? businessFeatures : platformFeatures;

  const industryLogos = [
    { name: 'Financial Services', icon: <Building2 className="w-5 h-5" /> },
    { name: 'Healthcare', icon: <Users className="w-5 h-5" /> },
    { name: 'E-commerce', icon: <Server className="w-5 h-5" /> },
    { name: 'Enterprise', icon: <Cloud className="w-5 h-5" /> }
  ];

  return (
    <section
      id="features-section"
      className="py-24 bg-gradient-to-br from-[#0f0c29] via-[#302b63] to-[#24243e] relative overflow-hidden"
    >
      {/* Background Effects */}
      <div className="absolute inset-0">
        {/* Floating Orbs */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '500ms' }} />

        {/* Animated Particles */}
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-purple-400 rounded-full opacity-20 animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${3 + Math.random() * 2}s`
            }}
          />
        ))}
      </div>

      {/* Circuit Grid Pattern */}
      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: `
          linear-gradient(rgba(139, 92, 246, 0.4) 1px, transparent 1px),
          linear-gradient(90deg, rgba(139, 92, 246, 0.4) 1px, transparent 1px),
          radial-gradient(circle at 25% 25%, rgba(59, 130, 246, 0.2) 2px, transparent 2px),
          radial-gradient(circle at 75% 75%, rgba(139, 92, 246, 0.2) 2px, transparent 2px)
        `,
          backgroundSize: '60px 60px, 60px 60px, 100px 100px, 100px 100px'
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className={`text-center mb-12 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
          <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-purple-500/20 to-blue-500/20 px-6 py-3 rounded-full mb-6 border border-purple-500/30">
            <Sparkles className="w-5 h-5 text-purple-400" />
            <span className="text-purple-300 font-medium">Complete Platform</span>
          </div>

          <h2 className="text-5xl lg:text-6xl font-bold mb-6 text-white">
            Everything You Need for
            <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400 bg-clip-text text-transparent">
              {" "}Enterprise AI
            </span>
          </h2>

          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            From consulting to deployment, powerful platform capabilities with enterprise-grade features
          </p>
        </div>

        {/* Tab Navigation */}
        <div className={`flex justify-center mb-12 transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
          <div className="bg-gradient-to-r from-white/10 to-white/5 backdrop-blur-2xl rounded-2xl p-2 inline-flex space-x-2 border border-white/20">
            <button
              onClick={() => setActiveTab('business')}
              className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 ${activeTab === 'business'
                  ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg'
                  : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}
            >
              <Target className="w-4 h-4 inline mr-2" />
              Business Value
            </button>
            <button
              onClick={() => setActiveTab('platform')}
              className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 ${activeTab === 'platform'
                  ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg'
                  : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}
            >
              <Code className="w-4 h-4 inline mr-2" />
              Platform Features
            </button>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {activeFeatures.map((feature, index) => (
            <div
              key={`${activeTab}-${index}`}
              className={`group relative transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
              style={{ transitionDelay: `${index * 100}ms` }} 
              onMouseLeave={() => setHoveredCard(null)}
            >
              {/* Card */}
              <div className="relative h-full bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-2xl rounded-3xl p-8 border border-white/20 hover:border-white/30 transition-all duration-300 group-hover:scale-105 overflow-hidden">
                {/* Subtle Hover Glow Effect */}
                <div className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-0 group-hover:opacity-10 transition-opacity duration-500 rounded-3xl`} />

                {/* Benefit Badge */}
                <div className="absolute top-4 right-4 bg-gradient-to-r from-black/40 to-black/30 backdrop-blur-sm px-3 py-1.5 rounded-full border border-white/10">
                  <span className="text-xs font-medium text-gray-300">{feature.benefit}</span>
                </div>

                {/* Icon */}
                <div className={`relative w-16 h-16 mb-6 rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center group-hover:scale-110 transition-all duration-300`}>
                  <div className="text-white">
                    {feature.icon}
                  </div>

                  {/* Icon Glow */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-0 group-hover:opacity-30 rounded-2xl blur-xl transition-opacity duration-500`} />
                </div>

                {/* Content */}
                <div className="relative z-10">
                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-white group-hover:to-gray-300 transition-all duration-300">
                    {feature.title}
                  </h3>
                  <p className="text-gray-300 text-sm leading-relaxed mb-6">
                    {feature.desc}
                  </p>

                  {/* Learn More Link */}
                  <div className="flex items-center space-x-2 text-sm text-purple-400 group-hover:text-purple-300 transition-colors duration-300">
                    <span className="font-medium">Learn more</span>
                    <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </div>
                </div>
              </div>

              {/* Active Indicator */}
              {hoveredCard === index && (
                <div className="absolute -top-3 -right-3 w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center shadow-2xl animate-bounce">
                  <CheckCircle className="w-4 h-4 text-white" />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Industry Trust Section */}
        <div className={`mt-20 transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
          <div className="bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-2xl rounded-3xl p-8 border border-white/20 hover:border-white/30 transition-all duration-300">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="text-center md:text-left">
                <h3 className="text-2xl font-bold text-white mb-2">Trusted Across Industries</h3>
                <p className="text-gray-300">Delivering AI solutions for regulated and complex environments</p>
              </div>

              <div className="flex flex-wrap gap-4 justify-center">
                {industryLogos.map((industry, idx) => (
                  <div
                    key={idx}
                    className="group flex items-center space-x-2 bg-gradient-to-r from-white/10 to-white/5 backdrop-blur-xl px-4 py-3 rounded-2xl border border-white/20 hover:border-purple-400/50 hover:scale-105 transition-all duration-300"
                  >
                    <div className="p-2 bg-gradient-to-r from-purple-500/20 to-blue-500/20 rounded-lg border border-purple-500/30 group-hover:scale-110 transition-transform duration-300">
                      <div className="text-purple-400 group-hover:text-purple-300 transition-colors">
                        {industry.icon}
                      </div>
                    </div>
                    <span className="text-sm font-medium text-gray-300 group-hover:text-white transition-colors">
                      {industry.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className={`text-center mt-12 transition-all duration-1000 delay-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
          <button className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 px-8 py-4 rounded-xl transition-all duration-300 font-semibold text-lg inline-flex items-center space-x-2 shadow-lg hover:shadow-purple-500/25 hover:scale-105 group text-white">
            <span>Explore Platform Capabilities</span>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default Home2FeaturesSection;